import React, { useMemo, useState } from 'react';
import { getLocale, useDispatch, useIntl } from 'umi';
import moment from 'moment';
import { trim } from 'lodash-es';
import { List, SearchBox, Segment } from 'tea-component';
import SvgIcon from '@home/components/SvgIcon';
import TextBubbles from '@home/components/TextBubbles';
import NoData from "@/pages/dashboard/components/NoData";
import { accentIncludes } from '@/pages/datalab/utils/accentMap';
import { shortcutOptionDefualt } from '@/pages/dashboard/components/NewFilter/FilterBase/shortcutOptionDefualt';
import { getExtraShortcut } from '@/pages/dashboard/components/NewFilter/FilterBase/getExtraShortcut';
import './index.scss';

export interface IQuickValue {
  tab: string;
  commonType?: string;
  eventType?: string;
  eventId?: string;
}

interface IProps {
  quickValue: IQuickValue;
  changeQuickValue: Function;
  commonDataList?: any[];
  latestDate: object;
  startOfTime: string;
  seasonInfo: any;
  handleChangeValue: Function;
  childRef?: React.Ref<HTMLDivElement>;
  type: string;
  eventTypeClick?: Function;
  changeClose?: boolean; // 用户选择日期变化时是否关闭弹窗, 默认为true 关闭弹窗
  commonTab?: React.ReactNode;
}

/**
 * 日期事件快捷选择组件
 * 用于提供日期和事件的快捷选择功能，支持通用选项和事件类型两种模式
 */
const DateEventShortcut = (props: IProps) => {
  // 从props中解构出需要的属性
  const {
    commonDataList: propsCommonDataList,  // 通用数据列表
    latestDate,  // 最晚日期
    startOfTime,  // 开始时间
    seasonInfo,  // 事件信息
    handleChangeValue,  // 处理值变化的函数
    childRef,  // 子组件引用
    eventTypeClick,  // 事件类型点击处理函数
    type,  // 类型
    changeClose,  // 关闭处理函数
    quickValue,  // 快捷值
    changeQuickValue,  // 更改快捷值的函数
    commonTab,
  } = props;
  // 使用国际化hook
  const { formatMessage } = useIntl();
  // 判断是否为英文环境
  const isEn = getLocale() === 'en-US';
  // 获取dispatch函数
  const dispatch = useDispatch();
  // 定义搜索值状态
  const [searchValue, setsearchValue] = useState('');
  // 从quickValue中解构出当前选中的标签页、通用类型、事件类型和事件ID
  const {
    tab: tabActiveTab = 'common',  // 当前激活的标签页，默认为'common'
    commonType: selectedcommonType = "",  // 选中的通用类型，默认为空字符串
    eventType: selectedEventType = 'all',  // 选中的事件类型，默认为'all'
    eventId: selectedEventChild = "",  // 选中的子事件ID，默认为空字符串
  } = quickValue || { tab: 'common', commonType: "", eventType: 'all', eventId: "" }; // 左侧快捷选择

  // 使用useMemo缓存计算结果，避免重复计算
  const { commonDataList, eventRenderDataList } = useMemo(() => {
    // 获取通用数据列表
    const commonDataList = propsCommonDataList || shortcutOptionDefualt(startOfTime, latestDate, seasonInfo, dispatch)?.[type];
    // 获取额外快捷选项
    const eventData = getExtraShortcut(seasonInfo);
    // 获取事件渲染数据列表
    const eventRenderDataList = eventData?.[type] || [];
    return {
      commonDataList,
      eventData,
      eventRenderDataList,
    };
  }, [dispatch, latestDate, propsCommonDataList, seasonInfo, startOfTime, type]);

  // 使用useMemo处理事件数据类型列表
  const eventDataTypeList = useMemo(() => {
    // 使用reduce方法对事件渲染数据进行分组处理
    const rusultArr = Object.values(eventRenderDataList?.reduce((acc: any, obj: any) => {
      const key = obj.event_type_name_en;
      // 初始化'all'分组
      if (!acc.all) {
        acc.all = {
          event_type_name: 'all',
          event_type_name_cn: "全部",
          event_type_name_en: "All",
          children: []
        };
      }
      // 初始化特定事件类型分组
      if (!acc[key]) {
        acc[key] = {
          event_type_name: key,
          event_type_name_en: obj?.event_type_name_en,
          // 后端取消了cn传参，但是all的时候还是要区分中英文的，所以前端中文字段保留
          event_type_name_cn: obj?.event_type_name_en,
          children: []
        };
        obj.isGroup = true;
      } else {
        obj.isGroup = false;
      }
      // 将对象添加到对应分组
      acc[key].children.push(obj);
      acc.all.children.push(obj);
      return acc;
    }, {}));

    // 将'all'分组放在列表首位
    const allItem = rusultArr?.find(item => item.event_type_name === 'all');
    const restItems = rusultArr?.filter(item => item.event_type_name !== 'all');
    const result = [allItem, ...restItems];
    return result;
  }, [eventRenderDataList]);

  // 使用useMemo处理实际的事件类型列表
  const realEventTypeList = useMemo(() => {
    const searchStr = trim(searchValue);
    if (!searchStr) {
      return eventDataTypeList;
    }
    if (!eventDataTypeList) {
      return undefined;
    }

    const matchChild = (child: { name?: string; nameCN?: string }) => {
      const name = isEn ? child.name : child.nameCN;
      return accentIncludes(name, searchStr);
    };

    return eventDataTypeList.reduce<typeof eventDataTypeList>((acc, item) => {
      const children = item?.children?.filter(matchChild) ?? [];
      if (children.length > 0) {
        acc.push({ ...(item || {}), children });
      }
      return acc;
    }, []);
  }, [eventDataTypeList, searchValue, isEn]);

  // 使用useMemo处理事件数据列表
  const eventDataList = useMemo(() => {
    const result = realEventTypeList?.find(item => item?.event_type_name === selectedEventType)?.children || [];
    return result;
  }, [realEventTypeList, selectedEventType]);

  // 返回组件的JSX结构
  return (
    <div className="dbl-rangepicker-left">
      {/* 标签页切换组件 */}
      <Segment
        className="dbl-rangepicker-left-segment"
        value={tabActiveTab}
        onChange={(value) => {
          changeQuickValue({
            tab: value,
            commonType: selectedcommonType,
            eventType: selectedEventType,
            eventId: selectedEventChild
          });
          setsearchValue('');
        }}
        options={[
          { text: formatMessage({ id: 'common_common' }), value: 'common' },
          { text: formatMessage({ id: 'common_event' }), value: 'event' },
        ]}
      />
      {/* 根据当前标签页显示不同的内容 */}
      {tabActiveTab === 'common' ? (commonTab || (
        <List type="option" className="dbl-common-box">
          {commonDataList?.map((item, index) => (
            <List.Item className={`dbl-common-box-item ${selectedcommonType === item?.type && 'dbl-rangepicker-shortcut-hover'}`}
              key={item?.name + index}
              onClick={() => {
                const newQuickValue = {
                  tab: tabActiveTab,
                  commonType: item?.type,
                  eventType: "all",
                  eventId: ""
                };
                changeQuickValue(newQuickValue);
                handleChangeValue(item?.onClick(), { type, tab: tabActiveTab, quickValue: newQuickValue });
                //  关闭时间组件弹出窗
                changeClose && childRef?.current?.setPopActive();
              }}>
              {isEn ? item?.name : item?.nameCN}
            </List.Item>
          ))}
        </List>
      )) : (
        <>
          {/* 搜索框 */}
          <SearchBox
            className="dbl-rangepicker-left-search"
            value={searchValue}
            onChange={(value: any) => {
              setsearchValue(value);
            }}
            placeholder={formatMessage({ id: 'common_search' })}
            onClick={(e) => {
              e?.stopPropagation();
              e?.target?.focus();
            }}
          />
          {/* 事件列表容器 */}
          {
            (eventRenderDataList?.length > 0 && realEventTypeList?.length > 0) ? <div className="dbl-rangepicker-left-box">
              {/* 左侧事件类型列表 */}
              <div className="dbl-left-list">
                <List type="option">
                  {realEventTypeList?.map((item, index) => (
                    <List.Item
                      key={item?.event_type_name_en + index}
                      className="dbl-left-item"
                      selected={selectedEventType === item?.event_type_name}
                      onClick={() => {
                        changeQuickValue({
                          tab: tabActiveTab,
                          commonType: "",
                          eventType: item?.event_type_name,
                          eventId: selectedEventChild
                        });
                      }}
                    >
                      <TextBubbles
                        className='dbl-left-text dbl-left-item-text-bubble-span dbl-left-item-text-bubble '
                        placement="right"
                        bubbleProps={{ overlayStyle: { zIndex: 99999999 } }}
                        text={
                          isEn ? item?.event_type_name_en : item?.event_type_name_cn
                        }
                      />
                      <SvgIcon type="point-right" className="dbl-left-icon" />
                    </List.Item>
                  ))}
                </List>
              </div>
              {/* 右侧具体事件列表 */}
              <div className="dbl-right-list">
                <List type="option">
                  {eventDataList?.map((item: any, index: number) => {
                    const name = isEn ? item?.name : item?.nameCN;
                    let dateDom = null;
                    try {
                      // 格式化事件日期
                      const startTime = moment(item.start_date).format(
                        'YYYY-MM-DD',
                      );
                      const endTime = moment(item.end_date).format(
                        'YYYY-MM-DD',
                      );
                      dateDom = (
                        <TextBubbles
                          className="dbl-right-item-text-bubble"
                          placement="right"
                          bubbleProps={{ overlayStyle: { zIndex: 99999999 } }}
                          text={
                            <div className="dbl-right-item-text dbl-right-item-text-date dbl-right-item-text-bubble-span">{`${startTime} ~ ${endTime}`}</div>
                          }
                        />
                      );
                    } catch {
                      console.warn(`${name} event date error`);
                    }

                    return (
                      <List.Item
                        className="dbl-right-item"
                        key={item.event_id + index}
                        selected={selectedEventChild === item?.event_id}
                        onClick={() => {
                          const newQuickValue = {
                            tab: tabActiveTab,
                            commonType: "",
                            eventType: selectedEventType,
                            eventId: item?.event_id
                          };
                          changeQuickValue(newQuickValue);
                          handleChangeValue(item?.onClick(), { type, tab: tabActiveTab, quickValue: newQuickValue, dateEvent: item });
                          eventTypeClick?.(item);
                          changeClose && childRef?.current?.setPopActive();
                        }}
                      >
                        <TextBubbles
                          className='dbl-right-item-text-bubble'
                          placement="right"
                          bubbleProps={{ overlayStyle: { zIndex: 99999999 } }}
                          text={
                            <div className='dbl-right-item-text dbl-right-item-text-bubble-span'
                              dangerouslySetInnerHTML={{
                                __html: !searchValue ? name : (name.replace(new RegExp(searchValue, 'gi'), '<span style="color: blue">$&</span>'))
                              }}></div>
                          }
                        />
                        {dateDom}
                      </List.Item>
                    );
                  })}
                </List>
              </div>
            </div> : <NoData className='dbl-rangepicker-left-nodata' text={formatMessage({ id: 'common_event_selector_nodata' })} />
          }
        </>
      )}
    </div>
  );
};

export default DateEventShortcut;
